import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../helpers/use-store';

interface UsePollUserStateOptions {
  intervalMs?: number;
  maxAttempts?: number;
}

interface UserStateSnapshot {
  planId?: string;
  credits: number;
  topupCredits: number;
  isActive: boolean;
}

const DEFAULT_INTERVAL_MS = 5_000;
const DEFAULT_MAX_ATTEMPTS = 12; // 12 * 5s = 1 min

/**
 * Polls `userStore.getLatestUser()` after an async backend action (checkout,
 * plan change, topup grant) until the user's subscription state visibly
 * changes — planId differs, credits jumped, or the subscription flipped to
 * active. Stops early on a detected change, or after `maxAttempts`. Calling
 * `startPolling()` again resets the baseline and restarts the loop.
 */
export const usePollUserState = ({
  intervalMs = DEFAULT_INTERVAL_MS,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
}: UsePollUserStateOptions = {}) => {
  const {
    dataStore: { userStore },
  } = useStore();

  const [isPolling, setIsPolling] = useState(false);
  const intervalIdRef = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (intervalIdRef.current !== null) {
      window.clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const startPolling = useCallback(() => {
    stop();

    const baseline: UserStateSnapshot = {
      planId: userStore.userSubscription.planId,
      credits: userStore.userSubscription.credits || 0,
      topupCredits: userStore.userSubscription.topupCredits || 0,
      isActive: userStore.isSubscriptionActive,
    };

    let attempt = 0;
    setIsPolling(true);

    intervalIdRef.current = window.setInterval(async () => {
      attempt += 1;
      await userStore.getLatestUser();

      const sub = userStore.userSubscription;
      const stateChanged =
        sub.planId !== baseline.planId ||
        (sub.credits || 0) > baseline.credits ||
        (sub.topupCredits || 0) > baseline.topupCredits ||
        (!baseline.isActive && userStore.isSubscriptionActive);

      if (stateChanged || attempt >= maxAttempts) {
        stop();
      }
    }, intervalMs);
  }, [intervalMs, maxAttempts, stop, userStore]);

  useEffect(() => stop, [stop]);

  return { startPolling, stopPolling: stop, isPolling };
};
